import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { getErrorMessage } from '@/shared/api/api-error';

import {
  decideApproval,
  getCatalogueHealth,
  getCatalogueHealthItems,
  getCountries,
  getCountry,
  getCountryPricing,
  getCountryReadiness,
  getCurrencies,
  createCurrency,
  setCountryStatus,
  setCurrencyStatus,
  updateCountrySettings,
  updateCurrency,
  getFxHistory,
  getFxRates,
  requestChannelFees,
  saveChannelOrder,
  getChannelDeletionCheck,
  deletePaymentChannel,
  requestFxOverride,
  revokeFxOverride,
  updatePaymentChannel,
  getPaymentChannels,
  getPricePreview,
  getPricingCalculation,
  getPricingHealth,
  setDraftParameter,
  setPaymentChannelStatus,
  submitDraft,
} from './pricing.api';
import type {
  PricingCalculationRequest,
  ChannelDetailsPayload,
  CountrySettingsPayload,
} from '../types/pricing.types';

export const pricingKeys = {
  all: ['pricing'] as const,
  countries: () => [...pricingKeys.all, 'countries'] as const,
  country: (countryId: number) => [...pricingKeys.all, 'country', countryId] as const,
  countryPricing: (countryId: number) => [...pricingKeys.country(countryId), 'pricing'] as const,
  channels: (countryId: number) => [...pricingKeys.country(countryId), 'channels'] as const,
  fx: (countryId: number) => [...pricingKeys.country(countryId), 'fx'] as const,
  fxHistory: (countryId: number, currencyCode: string, days: number) =>
    [...pricingKeys.fx(countryId), 'history', currencyCode, days] as const,
  health: (countryId: number) => [...pricingKeys.country(countryId), 'health'] as const,
  healthItems: (countryId: number, reason: string) =>
    [...pricingKeys.health(countryId), 'items', reason] as const,
  pricingHealth: (countryId: number, from: string, to: string) =>
    [...pricingKeys.country(countryId), 'pricing-health', from, to] as const,
  preview: (countryId: number, denominationId: number, includeDraft: boolean) =>
    [...pricingKeys.country(countryId), 'preview', denominationId, includeDraft] as const,
  readiness: (countryId: number) => [...pricingKeys.country(countryId), 'readiness'] as const,
  currencies: () => [...pricingKeys.all, 'currencies'] as const,
  calculation: (request: PricingCalculationRequest) =>
    [...pricingKeys.all, 'calculation', request] as const,
};

/**
 * Other features' root keys, kept literal: prices and channels feed market and
 * brands, and a request sent for approval appears in the approvals inbox.
 */
const DEPENDENT_KEYS = [['market'], ['brands'], ['approvals-inbox']] as const;

function useInvalidatePricing() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: pricingKeys.all });
    for (const key of DEPENDENT_KEYS) void queryClient.invalidateQueries({ queryKey: key });
  };
}

export function useCountries() {
  return useQuery({
    queryKey: pricingKeys.countries(),
    queryFn: ({ signal }) => getCountries(signal),
  });
}

export function useCountry(countryId: number | undefined) {
  return useQuery({
    queryKey: pricingKeys.country(countryId ?? 0),
    queryFn: ({ signal }) => getCountry(countryId as number, signal),
    enabled: countryId !== undefined,
  });
}

export function useCountryPricing(countryId: number) {
  return useQuery({
    queryKey: pricingKeys.countryPricing(countryId),
    queryFn: ({ signal }) => getCountryPricing(countryId, signal),
  });
}

export function usePaymentChannels(countryId: number) {
  return useQuery({
    queryKey: pricingKeys.channels(countryId),
    queryFn: ({ signal }) => getPaymentChannels(countryId, signal),
  });
}

export function useFxRates(countryId: number) {
  return useQuery({
    queryKey: pricingKeys.fx(countryId),
    queryFn: ({ signal }) => getFxRates(countryId, signal),
  });
}

export function useCatalogueHealth(countryId: number) {
  return useQuery({
    queryKey: pricingKeys.health(countryId),
    queryFn: ({ signal }) => getCatalogueHealth(countryId, signal),
  });
}

export function useCatalogueHealthItems(countryId: number, reason: string, enabled: boolean) {
  return useQuery({
    queryKey: pricingKeys.healthItems(countryId, reason),
    queryFn: ({ signal }) => getCatalogueHealthItems(countryId, reason, signal),
    enabled,
  });
}

export function usePricingHealth(countryId: number, from: string, to: string, enabled: boolean) {
  return useQuery({
    queryKey: pricingKeys.pricingHealth(countryId, from, to),
    queryFn: ({ signal }) => getPricingHealth(countryId, from, to, signal),
    enabled,
  });
}

export function usePricePreview(
  countryId: number,
  denominationId: number | null,
  includeDraft: boolean,
) {
  return useQuery({
    queryKey: pricingKeys.preview(countryId, denominationId ?? 0, includeDraft),
    queryFn: ({ signal }) =>
      getPricePreview(countryId, denominationId as number, includeDraft, signal),
    enabled: denominationId !== null,
  });
}

/**
 * Pricing Calculation Inspector. A live "now" view: never served from cache and
 * not retried — each call is audited, and a refusal is an answer, not an error.
 */
export function usePricingCalculation(request: PricingCalculationRequest | null) {
  return useQuery({
    queryKey: pricingKeys.calculation(request as PricingCalculationRequest),
    queryFn: ({ signal }) => getPricingCalculation(request as PricingCalculationRequest, signal),
    enabled: request !== null,
    staleTime: 0,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function useSetDraftParameter(countryId: number) {
  const invalidate = useInvalidatePricing();
  return useMutation({
    mutationFn: ({ parameter, value }: { parameter: string; value: number }) =>
      setDraftParameter(countryId, parameter, value),
    onSuccess: (_result, { parameter }) => {
      toast.success(`${parameter} saved to the draft.`);
      invalidate();
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, 'Could not save the draft.'));
    },
  });
}

export function useSubmitDraft(countryId: number) {
  const invalidate = useInvalidatePricing();
  return useMutation({
    mutationFn: (reason: string) => submitDraft(countryId, reason),
    onSuccess: () => {
      toast.success('Sent for approval. Another staff member must approve it.');
      invalidate();
    },
  });
}

export function useDecideApproval() {
  const invalidate = useInvalidatePricing();
  return useMutation({
    mutationFn: (input: {
      approvalId: number;
      decision: 'approve' | 'reject' | 'cancel';
      reason: string | null;
    }) => decideApproval(input.approvalId, input.decision, input.reason),
    onSuccess: (_result, { decision }) => {
      toast.success(
        decision === 'approve'
          ? 'Approved — the new pricing is live.'
          : decision === 'reject'
            ? 'Request rejected.'
            : 'Request withdrawn.',
      );
      invalidate();
    },
  });
}

/** Refetched every time the delete dialog opens: an order may have used the channel since. */
export function useChannelDeletionCheck(countryId: number, channelId: number) {
  return useQuery({
    queryKey: [...pricingKeys.channels(countryId), 'deletion', channelId] as const,
    queryFn: ({ signal }) => getChannelDeletionCheck(countryId, channelId, signal),
    staleTime: 0,
  });
}

export function useDeletePaymentChannel(countryId: number) {
  const invalidate = useInvalidatePricing();
  return useMutation({
    mutationFn: ({ channelId, reason }: { channelId: number; reason: string }) =>
      deletePaymentChannel(countryId, channelId, reason),
    onSuccess: () => {
      toast.success('Payment channel deleted.');
      invalidate();
    },
  });
}

export function useSetPaymentChannelStatus(countryId: number) {
  const invalidate = useInvalidatePricing();
  return useMutation({
    mutationFn: (input: { channelId: number; isActive: boolean; reason: string | null }) =>
      setPaymentChannelStatus(countryId, input.channelId, input.isActive, input.reason),
    onSuccess: (changed, { isActive }) => {
      toast.success(
        changed === 0
          ? 'Nothing to change.'
          : isActive
            ? 'Channel switched on.'
            : 'Channel switched off.',
      );
      invalidate();
    },
  });
}

// ------------------------------------------------- country settings (P2)

export function useCountryReadiness(countryId: number) {
  return useQuery({
    queryKey: pricingKeys.readiness(countryId),
    queryFn: ({ signal }) => getCountryReadiness(countryId, signal),
  });
}

/** Field errors (currency locked, unknown currency) are mapped onto the form by the caller. */
export function useUpdateCountrySettings(countryId: number) {
  const invalidate = useInvalidatePricing();
  return useMutation({
    mutationFn: (payload: CountrySettingsPayload) => updateCountrySettings(countryId, payload),
    onSuccess: () => {
      toast.success('Country settings saved.');
      invalidate();
    },
  });
}

export function useSetCountryStatus(countryId: number) {
  const invalidate = useInvalidatePricing();
  return useMutation({
    mutationFn: (input: { isActive: boolean; reason: string; confirmName: string | null }) =>
      setCountryStatus(countryId, input),
    onSuccess: (_result, input) => {
      toast.success(
        input.isActive ? 'Country activated.' : 'Country deactivated: new purchases are stopped.',
      );
      invalidate();
    },
  });
}

// -------------------------------------------------------------- currencies

export function useCurrencies() {
  return useQuery({
    queryKey: pricingKeys.currencies(),
    queryFn: ({ signal }) => getCurrencies(signal),
  });
}

export function useCreateCurrency() {
  const invalidate = useInvalidatePricing();
  return useMutation({
    mutationFn: createCurrency,
    onSuccess: (code) => {
      toast.success(`Currency ${code} created.`);
      invalidate();
    },
  });
}

export function useUpdateCurrency() {
  const invalidate = useInvalidatePricing();
  return useMutation({
    mutationFn: (input: { code: string; name: string; symbol: string | null; reason: string }) =>
      updateCurrency(input.code, { name: input.name, symbol: input.symbol, reason: input.reason }),
    onSuccess: () => {
      toast.success('Currency saved.');
      invalidate();
    },
  });
}

export function useSetCurrencyStatus() {
  const invalidate = useInvalidatePricing();
  return useMutation({
    mutationFn: (input: {
      code: string;
      isActive: boolean;
      reason: string;
      acknowledgeUsage: boolean;
    }) =>
      setCurrencyStatus(input.code, {
        isActive: input.isActive,
        reason: input.reason,
        acknowledgeUsage: input.acknowledgeUsage,
      }),
    onSuccess: (_result, input) => {
      toast.success(input.isActive ? `${input.code} activated.` : `${input.code} deactivated.`);
      invalidate();
    },
    onError: (error) => toast.error(getErrorMessage(error, 'Could not change the currency.')),
  });
}

export function useFxHistory(countryId: number, currencyCode: string, days: number) {
  return useQuery({
    queryKey: pricingKeys.fxHistory(countryId, currencyCode, days),
    queryFn: ({ signal }) => getFxHistory(countryId, currencyCode, days, signal),
    placeholderData: (previous) => previous,
  });
}

/** Errors (too long, a request already waiting) are shown in the dialog by the caller. */
export function useRequestFxOverride(countryId: number, currencyCode: string) {
  const invalidate = useInvalidatePricing();
  return useMutation({
    mutationFn: (payload: { rate: number; durationHours: number; reason: string }) =>
      requestFxOverride(countryId, currencyCode, payload),
    onSuccess: () => {
      toast.success('Manual rate requested. An Admin must approve it.');
      invalidate();
    },
  });
}

export function useRevokeFxOverride(countryId: number, currencyCode: string) {
  const invalidate = useInvalidatePricing();
  return useMutation({
    mutationFn: (reason: string) => revokeFxOverride(countryId, currencyCode, reason),
    onSuccess: () => {
      toast.success('Manual rate ended. The feed rate applies again.');
      invalidate();
    },
  });
}

export function useUpdatePaymentChannel(countryId: number, channelId: number) {
  const invalidate = useInvalidatePricing();
  return useMutation({
    mutationFn: (payload: ChannelDetailsPayload) =>
      updatePaymentChannel(countryId, channelId, payload),
    onSuccess: (result) => {
      toast.success(result.changed === 0 ? 'Nothing to change.' : 'Channel updated.');
      invalidate();
    },
  });
}

export function useSaveChannelOrder(countryId: number) {
  const queryClient = useQueryClient();
  const invalidate = useInvalidatePricing();
  return useMutation({
    mutationFn: ({ channelIds, reason }: { channelIds: number[]; reason: string }) =>
      saveChannelOrder(countryId, channelIds, reason),
    onSuccess: (changed) => {
      toast.success(changed === 0 ? 'The order was already saved.' : 'Checkout order saved.');
      invalidate();
    },
    // A 409 means a channel was added or removed meanwhile — reload the list.
    onError: () =>
      void queryClient.invalidateQueries({ queryKey: pricingKeys.channels(countryId) }),
  });
}

export function useRequestChannelFees(countryId: number, channelId: number) {
  const invalidate = useInvalidatePricing();
  return useMutation({
    mutationFn: (payload: { percentFee: number; fixedFee: number; reason: string }) =>
      requestChannelFees(countryId, channelId, payload),
    onSuccess: () => {
      toast.success('Fee change requested. An Admin must approve it.');
      invalidate();
    },
  });
}
